import type { Building } from "building-types/building/buildingTypes";
import getTargetedOpeningSettings from "../elements/openings/getTargetedOpeningSettings";
import { makeBuildingWallFraming } from "../elements/walls/makeBuildingWallFraming";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

export function select({
  levels,
  walls,
  planarGraphPerimeters,
  settings,
  settingsSets,
  optionChoices,
  windowPlacements,
  doorPlacements,
  enabledFeatures,
  doc: { settings: docSettings, entitySettings, headerAssemblies, framingComponents },
}: Pick<
  Building,
  | "levels"
  | "walls"
  | "planarGraphPerimeters"
  | "settings"
  | "settingsSets"
  | "optionChoices"
  | "windowPlacements"
  | "doorPlacements"
  | "enabledFeatures"
  | "doc"
>) {
  return {
    levels,
    walls,
    planarGraphPerimeters,
    wallCatalogSettings: settings.wallCatalog,
    wallFramingSettings: settings.wallFraming,
    headerFramingSettings: settings.headerFraming,
    docSettings,
    entitySettings,
    headerAssemblies,
    framingComponents,
    settingsSets,
    optionChoices,
    windowPlacements,
    doorPlacements,
    settings,
    wallPlanes: enabledFeatures.wallPlanes ?? false,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  wallPlanes: "shallow",
});

function generate(params: ReturnType<typeof select>): Pick<Building, "wallFramingSystems"> {
  const {
    levels,
    walls,
    planarGraphPerimeters,
    wallCatalogSettings,
    wallFramingSettings,
    headerFramingSettings,
    docSettings,
    entitySettings,
    headerAssemblies,
    framingComponents,
    settingsSets,
    optionChoices,
    windowPlacements,
    doorPlacements,
    settings,
    wallPlanes,
  } = params;

  if (wallPlanes) {
    return {
      wallFramingSystems: {},
    };
  }

  const targetedOpeningSettings = getTargetedOpeningSettings(
    {
      settingsSets,
      optionChoices,
      windowPlacements,
      doorPlacements,
      settings,
    },
    { settings: docSettings, entitySettings },
  );

  const wallFramingSystems = makeBuildingWallFraming({
    levels,
    walls,
    planarGraphPerimeters,
    wallCatalogSettings,
    headerFramingSettings,
    wallFramingSettings,
    targetedOpeningSettings,
    headerAssemblies,
    framingComponents,
  });

  return {
    wallFramingSystems,
  };
}

export default memo(select, generate, compare);
